/**
 * Admin audit log — recent platform-admin actions in a dense DataTable. An optional action filter
 * (applied on submit) narrows the query server-side.
 */
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import type { AdminAuditRow } from '@beam/api-types';
import { adminListAudit, AdminApiError } from '../../src/api/adminClient';
import { Screen, Text, Card, Field, Button, DataTable, type Column } from '../../src/components/ui';
import { AdminHeader } from '../../src/components/admin/AdminHeader';
import { theme } from '../../src/theme';

function shortId(value: string | null): string {
  if (!value) return '—';
  return value.length > 8 ? value.slice(0, 8) : value;
}

function formatAt(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

export default function AdminAuditScreen() {
  const [input, setInput] = useState('');
  const [action, setAction] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'audit', action],
    queryFn: () => adminListAudit({ action: action || undefined, limit: 200 }),
  });

  const columns: Column<AdminAuditRow>[] = [
    { key: 'createdAt', header: 'When', flex: 1.6, render: (r) => <Text variant="small" color="secondary" numberOfLines={1}>{formatAt(r.createdAt)}</Text> },
    { key: 'action', header: 'Action', flex: 1.6, render: (r) => <Text variant="bodyStrong" numberOfLines={1}>{r.action}</Text> },
    { key: 'adminId', header: 'Admin', flex: 1, render: (r) => <Text variant="data" numberOfLines={1}>{shortId(r.adminId)}</Text> },
    { key: 'targetOrgId', header: 'Org', flex: 1, render: (r) => <Text variant="data" numberOfLines={1}>{shortId(r.targetOrgId)}</Text> },
    { key: 'ip', header: 'IP', flex: 1.2, render: (r) => <Text variant="data" color="secondary" numberOfLines={1}>{r.ip ?? '—'}</Text> },
  ];

  return (
    <Screen scroll maxWidth={null}>
      <AdminHeader active="audit" />

      <View style={styles.toolbar}>
        <View style={styles.filterField}>
          <Field
            label="Filter by action"
            value={input}
            onChangeText={setInput}
            autoCapitalize="none"
            autoCorrect={false}
            placeholder="e.g. license.suspend"
            returnKeyType="search"
            onSubmitEditing={() => setAction(input.trim())}
          />
        </View>
        <Button title="Apply" size="md" variant="secondary" fullWidth={false} onPress={() => setAction(input.trim())} style={styles.toolbarBtn} />
        {action ? (
          <Button title="Clear" size="md" variant="ghost" fullWidth={false} onPress={() => { setInput(''); setAction(''); }} style={styles.toolbarBtn} />
        ) : null}
      </View>

      {isLoading ? (
        <Card>
          <Text color="secondary">Loading audit log…</Text>
        </Card>
      ) : error ? (
        <Card>
          <Text color="danger">{error instanceof AdminApiError || error instanceof Error ? error.message : 'Failed to load audit log.'}</Text>
        </Card>
      ) : !data || data.length === 0 ? (
        <Card>
          <Text color="secondary">No audit entries.</Text>
        </Card>
      ) : (
        <Card style={styles.tableCard}>
          <Text variant="small" color="secondary">
            {data.length} entr{data.length === 1 ? 'y' : 'ies'}
          </Text>
          <DataTable columns={columns} rows={data} keyExtractor={(r) => r.id} />
        </Card>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  toolbar: { flexDirection: 'row', alignItems: 'flex-end', gap: theme.spacing.md, flexWrap: 'wrap' },
  filterField: { flex: 1, minWidth: 240 },
  toolbarBtn: { alignSelf: 'flex-end' },
  tableCard: { gap: theme.spacing.sm },
});
